import React, { useState, useEffect, useCallback } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { PageHeader, PageDescription } from '../main/LayoutStyles';
import { StyledButton, AlertBox } from '../main/CommonStyles';
import {
    AvailabilityForm,
    TimeSlotter,
    CalendarCard,
    SavedSlotsCard,
    AddSlotsCard
} from '../specialist/SpecialistPanelStyles';
import apiClient from '../services/api';
import { Loader2, AlertTriangle, Clock, Plus, Trash2, Video, Building, User, Coffee } from 'lucide-react';

const formatDateToYYYYMMDD = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toYearMonth = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
}

// Funkcja pomocnicza do sprawdzania nakładania się terminów
const hasOverlap = (slots) => {
    if (slots.length <= 1) return false;
    const timeToMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
    };
    const intervals = slots.map(slot => ({
        start: timeToMinutes(slot.startTime),
        end: timeToMinutes(slot.endTime)
    }));
    for(const interval of intervals) {
        if (interval.start >= interval.end) return true;
    }
    intervals.sort((a, b) => a.start - b.start);
    for (let i = 1; i < intervals.length; i++) {
        if (intervals[i].start < intervals[i - 1].end) return true;
    }
    return false;
};

const PsychologistAvailability = () => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [timeSlots, setTimeSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [activeMonth, setActiveMonth] = useState(toYearMonth(new Date()));
    const [allMonthSlots, setAllMonthSlots] = useState([]);
    const [loadingSaved, setLoadingSaved] = useState(false);
    const [deletingSlotId, setDeletingSlotId] = useState(null);

    const fetchAllSlots = useCallback(async (currentMonth) => {
        setLoadingSaved(true);
        try {
            const cacheBuster = new Date().getTime();
            const response = await apiClient.get(`/appointments/my-availability?month=${currentMonth}&_=${cacheBuster}`);
            setAllMonthSlots(response.data);
        } catch (err) {
            setError("Nie udało się pobrać Twojego grafiku.");
            console.error("Nie udało się pobrać grafiku.", err);
        } finally {
            setLoadingSaved(false);
        }
    }, []);

    useEffect(() => {
        fetchAllSlots(activeMonth);
    }, [activeMonth, fetchAllSlots]);

    useEffect(() => {
        setTimeSlots([]);
    }, [selectedDate]);

    const handleSlotChange = (index, field, value) => {
        const newTimeSlots = [...timeSlots];
        newTimeSlots[index][field] = value;
        setTimeSlots(newTimeSlots);
    };

    const addTimeSlot = () => {
        let newStartTime = '08:00';
        let newEndTime = '09:00';
        let newAppointmentType = 'ONLINE';

        const slotsForDay = [
            ...allMonthSlots.filter(s => formatDateToYYYYMMDD(new Date(s.startTime)) === formatDateToYYYYMMDD(selectedDate)),
            ...timeSlots.map(ts => ({
                startTime: `${formatDateToYYYYMMDD(selectedDate)}T${ts.startTime}`,
                endTime: `${formatDateToYYYYMMDD(selectedDate)}T${ts.endTime}`,
                appointmentType: ts.appointmentType
            }))
        ].sort((a, b) => new Date(a.endTime) - new Date(b.endTime));

        const lastSlot = slotsForDay[slotsForDay.length - 1];

        if (lastSlot) {
            newAppointmentType = lastSlot.appointmentType;
            const lastEndTime = new Date(lastSlot.endTime);
            const newStartDate = new Date(lastEndTime.getTime());
            const newEndDate = new Date(newStartDate.getTime() + 60 * 60 * 1000);

            newStartTime = newStartDate.toTimeString().substring(0, 5);
            newEndTime = newEndDate.toTimeString().substring(0, 5);
        } else {
            const today = new Date();
            if (formatDateToYYYYMMDD(selectedDate) === formatDateToYYYYMMDD(today)) {
                const nextHour = today.getHours() + 1;
                if (nextHour < 24) {
                    newStartTime = `${String(nextHour).padStart(2, '0')}:00`;
                    newEndTime = `${String(nextHour + 1).padStart(2, '0')}:00`;
                }
            }
        }

        setTimeSlots(prevSlots => [...prevSlots, { startTime: newStartTime, endTime: newEndTime, appointmentType: newAppointmentType }]);
    };

    const removeTimeSlot = (index) => {
        const newTimeSlots = timeSlots.filter((_, i) => i !== index);
        setTimeSlots(newTimeSlots);
    };

    const handleDeleteSlot = async (slotId) => {
        if (!window.confirm("Czy na pewno chcesz usunąć ten wolny termin? Ta akcja jest nieodwracalna.")) {
            return;
        }
        setDeletingSlotId(slotId);
        setError('');
        try {
            await apiClient.delete(`/appointments/availability/${slotId}`);
            fetchAllSlots(activeMonth);
        } catch(err) {
            setError(err.response?.data?.message || "Nie udało się usunąć terminu. Być może jest już zarezerwowany.");
        } finally {
            setDeletingSlotId(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        if (timeSlots.length === 0) {
            setError("Proszę dodać przynajmniej jeden przedział czasowy.");
            setLoading(false);
            return;
        }

        const today = new Date();
        today.setHours(0,0,0,0);
        if (selectedDate < today) {
            setError("Nie można ustawiać dostępności dla dat przeszłych.");
            setLoading(false);
            return;
        }

        const now = new Date();
        if(formatDateToYYYYMMDD(selectedDate) === formatDateToYYYYMMDD(now)) {
            for(const slot of timeSlots) {
                const [hour, minute] = slot.startTime.split(':');
                if (parseInt(hour, 10) < now.getHours() || (parseInt(hour, 10) === now.getHours() && parseInt(minute, 10) < now.getMinutes())) {
                    setError(`Nie można dodać terminu o ${slot.startTime}, ponieważ ta godzina już minęła.`);
                    setLoading(false);
                    return;
                }
            }
        }

        const freeSlotsForDay = allMonthSlots
            .filter(slot => !slot.booked && formatDateToYYYYMMDD(new Date(slot.startTime)) === formatDateToYYYYMMDD(selectedDate))
            .map(slot => ({
                startTime: new Date(slot.startTime).toTimeString().substring(0, 5),
                endTime: new Date(slot.endTime).toTimeString().substring(0, 5)
            }));

        const allProposedSlots = [...freeSlotsForDay, ...timeSlots];

        if (hasOverlap(allProposedSlots)) {
            setError("Wykryto nakładające się lub nieprawidłowe przedziały czasowe. Popraw godziny, aby się nie pokrywały i aby czas rozpoczęcia był wcześniejszy niż zakończenia.");
            setLoading(false);
            return;
        }

        try {
            const requestBody = {
                date: formatDateToYYYYMMDD(selectedDate),
                timeSlots: timeSlots,
            };
            await apiClient.post('/appointments/availability', requestBody);
            setSuccess(`Pomyślnie dodano dostępne terminy na dzień ${selectedDate.toLocaleDateString()}.`);
            setTimeSlots([]);
            fetchAllSlots(activeMonth);
        } catch (err) {
            setError(err.response?.data?.message || 'Nie udało się zapisać dostępności.');
        } finally {
            setLoading(false);
        }
    };

    const tileDisabled = ({ date, view }) => {
        if (view === 'month') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date < today;
        }
        return false;
    };

    const tileContent = ({ date, view }) => {
        if (view === 'month') {
            const dateStr = formatDateToYYYYMMDD(date);
            const hasSlots = allMonthSlots.some(slot => slot.startTime.startsWith(dateStr));
            return hasSlots ? <div className="dot"></div> : null;
        }
        return null;
    };

    const slotsForSelectedDay = allMonthSlots.filter(slot =>
        formatDateToYYYYMMDD(new Date(slot.startTime)) === formatDateToYYYYMMDD(selectedDate)
    );

    // KLUCZOWA POPRAWKA: Użycie "slot.booked" zamiast "slot.isBooked"
    const bookedSlots = slotsForSelectedDay.filter(s => s.booked);
    const freeSlots = slotsForSelectedDay.filter(s => !s.booked);

    return (
        <div>
            <PageHeader>Grafik Dnia</PageHeader>
            <PageDescription>Przeglądaj swój plan na wybrany dzień. Zarządzaj wolnymi terminami lub dodaj nowe przedziały dostępności.</PageDescription>

            {error && <AlertBox type="error" style={{marginBottom: '1rem'}}><AlertTriangle />{error}</AlertBox>}
            {success && <AlertBox type="success" style={{marginBottom: '1rem'}}>{success}</AlertBox>}

            <AvailabilityForm onSubmit={handleSubmit}>
                <CalendarCard>
                    <label>1. Wybierz dzień</label>
                    <Calendar
                        onChange={setSelectedDate}
                        value={selectedDate}
                        locale="pl-PL"
                        onActiveStartDateChange={({ activeStartDate }) => setActiveMonth(toYearMonth(activeStartDate))}
                        tileContent={tileContent}
                        tileDisabled={tileDisabled}
                    />
                </CalendarCard>

                <SavedSlotsCard>
                    <h4>Plan na {selectedDate.toLocaleDateString('pl-PL')}</h4>
                    {loadingSaved ? <Loader2 className="icon-spin" style={{margin: '1rem auto'}}/> : (
                        <div className="slots-scroll-area custom-scrollbar">
                            <h5 style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}><User size={18}/> Zarezerwowane wizyty</h5>
                            {bookedSlots.length > 0 ? (
                                <ul>{bookedSlots.map(slot => (
                                    <li key={slot.id} style={{backgroundColor: '#eef2ff', borderLeft: '3px solid #6366f1'}}>
                                        <span style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                            {slot.appointmentType === 'ONLINE' ? <Video size={16}/> : <Building size={16}/>}
                                            {new Date(slot.startTime).toLocaleTimeString('pl-PL', {hour: '2-digit', minute: '2-digit'})}
                                            - <strong>{slot.patientUsername}</strong>
                                        </span>
                                    </li>
                                ))}</ul>
                            ) : <p>Brak zarezerwowanych wizyt.</p>}

                            <h5 style={{marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}><Coffee size={18}/> Wolne terminy</h5>
                            {freeSlots.length > 0 ? (
                                <ul>{freeSlots.map(slot => (
                                    <li key={slot.id}>
                                        <span style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                            {slot.appointmentType === 'ONLINE' ? <Video size={16} title="Online"/> : <Building size={16} title="Stacjonarnie"/>}
                                            {new Date(slot.startTime).toLocaleTimeString('pl-PL', {hour: '2-digit', minute: '2-digit'})} - {new Date(slot.endTime).toLocaleTimeString('pl-PL', {hour: '2-digit', minute: '2-digit'})}
                                        </span>
                                        <button type="button" onClick={() => handleDeleteSlot(slot.id)} disabled={deletingSlotId === slot.id} title="Usuń wolny termin">
                                            {deletingSlotId === slot.id ? <Loader2 size={16} className="icon-spin" /> : <Trash2 size={16}/>}
                                        </button>
                                    </li>
                                ))}</ul>
                            ) : <p>Brak wolnych terminów.</p>}
                        </div>
                    )}
                </SavedSlotsCard>

                <AddSlotsCard>
                    <label>2. Dodaj nowe przedziały czasowe</label>
                    <div className='slots-scroll-area custom-scrollbar' style={{paddingRight: 0}}>
                        {timeSlots.length > 0 ? timeSlots.map((slot, index) => (
                            <TimeSlotter key={index}>
                                <input
                                    type="time"
                                    value={slot.startTime}
                                    onChange={(e) => handleSlotChange(index, 'startTime', e.target.value)}
                                    required
                                />
                                <span>-</span>
                                <input
                                    type="time"
                                    value={slot.endTime}
                                    onChange={(e) => handleSlotChange(index, 'endTime', e.target.value)}
                                    required
                                />
                                <select value={slot.appointmentType} onChange={(e) => handleSlotChange(index, 'appointmentType', e.target.value)}>
                                    <option value="ONLINE">Online</option>
                                    <option value="IN_PERSON">Stacjonarnie</option>
                                </select>
                                <button type="button" onClick={() => removeTimeSlot(index)} title="Usuń przedział">
                                    <Trash2 size={18} />
                                </button>
                            </TimeSlotter>
                        )) : <p style={{color: '#999', textAlign: 'center'}}>Kliknij przycisk poniżej, aby dodać pierwszy termin.</p>}
                    </div>
                    <div style={{marginTop: 'auto', paddingTop: '1rem'}}>
                        <StyledButton type="button" $variant="outline" onClick={addTimeSlot} style={{ alignSelf: 'flex-start' }}>
                            <Plus size={16} /> Dodaj kolejny przedział
                        </StyledButton>
                    </div>
                </AddSlotsCard>

                <div className="submit-container">
                    <StyledButton type="submit" disabled={loading || timeSlots.length === 0}>
                        {loading ? <Loader2 className="icon-spin" /> : 'Zapisz nowe terminy'}
                    </StyledButton>
                </div>
            </AvailabilityForm>
        </div>
    );
};

export default PsychologistAvailability;